import { useReducer, useEffect } from "react";

// State constants
export const WALKUP_STATES = {
	OTP_VERIFIED: "otp_verified",
	RESOLVING_USER: "resolving_user",
	NEEDS_PROFILE: "needs_profile",
	NEEDS_CARD: "needs_card",
	SETTING_UP_CARD: "setting_up_card",
	PAYMENT_COMPLETE: "payment_complete",
	UNLOCKING: "unlocking",
	SESSION_ACTIVE: "session_active",
	ERROR: "error",
};

// Action types
const ACTIONS = {
	RESOLVE_USER_START: "resolve_user_start",
	RESOLVE_USER_SUCCESS: "resolve_user_success",
	RESOLVE_USER_ERROR: "resolve_user_error",
	PROFILE_UPDATED: "profile_updated",
	CARD_SETUP_START: "card_setup_start",
	CARD_SETUP_SUCCESS: "card_setup_success",
	CARD_SETUP_ERROR: "card_setup_error",
	PAYMENT_COMPLETE: "payment_complete",
	UNLOCK_START: "unlock_start",
	UNLOCK_SUCCESS: "unlock_success",
	UNLOCK_ERROR: "unlock_error",
	SET_ERROR: "set_error",
	CLEAR_ERROR: "clear_error",
	RESET: "reset",
};

// Initial state
const initialState = {
	state: WALKUP_STATES.OTP_VERIFIED,
	user: null,
	booth: null,
	session: null,
	setupIntent: null,
	userStatus: null,
	error: null,
	loading: false,
};

// State reducer
function walkupReducer(state, action) {
	switch (action.type) {
		case ACTIONS.RESOLVE_USER_START:
			return {
				...state,
				state: WALKUP_STATES.RESOLVING_USER,
				loading: true,
				error: null,
			};

		case ACTIONS.RESOLVE_USER_SUCCESS:
			const { status, user, boothInfo, setupIntent } = action.payload;

			let nextState;
			if (status === "needs_profile") {
				nextState = WALKUP_STATES.NEEDS_PROFILE;
			} else if (status === "needs_card") {
				nextState = WALKUP_STATES.NEEDS_CARD;
			} else if (status === "success") {
				// User has valid payment method - proceed to payment flow
				nextState = WALKUP_STATES.PAYMENT_COMPLETE;
			} else {
				// Unknown status - default to card setup for safety
				console.warn(
					"Unknown user status:",
					status,
					"defaulting to NEEDS_CARD"
				);
				nextState = WALKUP_STATES.NEEDS_CARD;
			}

			return {
				...state,
				state: nextState,
				user,
				booth: boothInfo,
				setupIntent,
				userStatus: status,
				loading: false,
				error: null,
			};

		case ACTIONS.RESOLVE_USER_ERROR:
			return {
				...state,
				state: WALKUP_STATES.ERROR,
				loading: false,
				error: action.payload,
			};

		case ACTIONS.PROFILE_UPDATED:
			// After profile update, determine next state based on whether user has payment method
			const hasPaymentMethod = state.user?.hasPaymentMethod;
			return {
				...state,
				user: { ...state.user, ...action.payload },
				state: hasPaymentMethod
					? WALKUP_STATES.PAYMENT_COMPLETE
					: WALKUP_STATES.NEEDS_CARD,
			};

		case ACTIONS.CARD_SETUP_START:
			return {
				...state,
				state: WALKUP_STATES.SETTING_UP_CARD,
				loading: true,
				error: null,
			};

		case ACTIONS.CARD_SETUP_SUCCESS:
			return {
				...state,
				state: WALKUP_STATES.PAYMENT_COMPLETE,
				loading: false,
				error: null,
			};

		case ACTIONS.CARD_SETUP_ERROR:
			return {
				...state,
				state: WALKUP_STATES.NEEDS_CARD,
				loading: false,
				error: action.payload,
			};

		case ACTIONS.PAYMENT_COMPLETE:
			return {
				...state,
				state: WALKUP_STATES.PAYMENT_COMPLETE,
				session: action.payload,
				loading: false,
				error: null,
			};

		case ACTIONS.UNLOCK_START:
			return {
				...state,
				state: WALKUP_STATES.UNLOCKING,
				loading: true,
				error: null,
			};

		case ACTIONS.UNLOCK_SUCCESS:
			return {
				...state,
				state: WALKUP_STATES.SESSION_ACTIVE,
				session: action.payload,
				loading: false,
				error: null,
			};

		case ACTIONS.UNLOCK_ERROR:
			return {
				...state,
				state: WALKUP_STATES.ERROR,
				loading: false,
				error: action.payload,
			};

		case ACTIONS.SET_ERROR:
			return {
				...state,
				state: WALKUP_STATES.ERROR,
				error: action.payload,
				loading: false,
			};

		case ACTIONS.CLEAR_ERROR:
			return {
				...state,
				error: null,
			};

		case ACTIONS.RESET:
			return initialState;

		default:
			return state;
	}
}

// Custom hook
export function useWalkUpFlow(boothId) {
	const [state, dispatch] = useReducer(walkupReducer, initialState);

	// Persist critical state to sessionStorage for recovery
	useEffect(() => {
		if (state.user || state.booth || state.session) {
			const persistData = {
				user: state.user,
				booth: state.booth,
				session: state.session,
				currentState: state.state,
			};
			sessionStorage.setItem("walkup_flow_state", JSON.stringify(persistData));
		}
	}, [state.user, state.booth, state.session, state.state]);

	// Restore state on mount if available
	useEffect(() => {
		const stored = sessionStorage.getItem("walkup_flow_state");
		if (stored) {
			try {
				const parsed = JSON.parse(stored);
				// Only restore if we have the same phone/booth combo
				if (parsed.booth?.id === boothId && parsed.user) {
					dispatch({
						type: ACTIONS.RESOLVE_USER_SUCCESS,
						payload: {
							status: "success",
							user: parsed.user,
							boothInfo: parsed.booth,
							setupIntent: null,
						},
					});

					if (parsed.session) {
						dispatch({
							type: ACTIONS.UNLOCK_SUCCESS,
							payload: parsed.session,
						});
					}
				}
			} catch (error) {
				console.warn("Failed to restore walkup flow state:", error);
				sessionStorage.removeItem("walkup_flow_state");
			}
		}
	}, [boothId]);

	// Action creators
	const actions = {
		resolveUserStart: () => dispatch({ type: ACTIONS.RESOLVE_USER_START }),

		resolveUserSuccess: (payload) =>
			dispatch({ type: ACTIONS.RESOLVE_USER_SUCCESS, payload }),

		resolveUserError: (error) =>
			dispatch({ type: ACTIONS.RESOLVE_USER_ERROR, payload: error }),

		profileUpdated: (userUpdates) =>
			dispatch({ type: ACTIONS.PROFILE_UPDATED, payload: userUpdates }),

		cardSetupStart: () => dispatch({ type: ACTIONS.CARD_SETUP_START }),

		cardSetupSuccess: () => dispatch({ type: ACTIONS.CARD_SETUP_SUCCESS }),

		cardSetupError: (error) =>
			dispatch({ type: ACTIONS.CARD_SETUP_ERROR, payload: error }),

		paymentComplete: (session) =>
			dispatch({ type: ACTIONS.PAYMENT_COMPLETE, payload: session }),

		unlockStart: () => dispatch({ type: ACTIONS.UNLOCK_START }),

		unlockSuccess: (session) =>
			dispatch({ type: ACTIONS.UNLOCK_SUCCESS, payload: session }),

		unlockError: (error) =>
			dispatch({ type: ACTIONS.UNLOCK_ERROR, payload: error }),

		setError: (error) => dispatch({ type: ACTIONS.SET_ERROR, payload: error }),

		clearError: () => dispatch({ type: ACTIONS.CLEAR_ERROR }),

		reset: () => {
			sessionStorage.removeItem("walkup_flow_state");
			dispatch({ type: ACTIONS.RESET });
		},
	};

	return {
		// State
		currentState: state.state,
		user: state.user,
		booth: state.booth,
		session: state.session,
		setupIntent: state.setupIntent,
		error: state.error,
		loading: state.loading,

		// Actions
		...actions,

		// Computed properties
		isLoading: state.loading,
		hasError: !!state.error,
		isReady: state.state === WALKUP_STATES.SESSION_ACTIVE,
		needsProfile: state.state === WALKUP_STATES.NEEDS_PROFILE,
		needsCard:
			state.state === WALKUP_STATES.NEEDS_CARD ||
			state.state === WALKUP_STATES.SETTING_UP_CARD,
		isSettingUpCard: state.state === WALKUP_STATES.SETTING_UP_CARD,
		isUnlocking: state.state === WALKUP_STATES.UNLOCKING,
	};
}
